import React, { memo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import { useTheme } from '../contexts/ThemeContext';
import { getLayoutMode, shouldShowShellHeader, shouldUseSurface } from '../routing/layoutModes';

const isEditableElement = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName.toLowerCase();
  return target.isContentEditable || tagName === 'input' || tagName === 'textarea' || tagName === 'select';
};

const Layout: React.FC<{ children: React.ReactNode; userRole?: string }> = memo(({ children, userRole }) => {
  const { getThemeClasses, getThemeStyles } = useTheme();
  const location = useLocation();
  const themeClasses = getThemeClasses();
  const themeStyles = getThemeStyles();
  const layoutMode = getLayoutMode(location.pathname);
  const showHeader = shouldShowShellHeader(layoutMode);
  const useSurface = shouldUseSurface(layoutMode);
  const isAuthMode = layoutMode === 'auth';
  const isFullBleedMode = layoutMode === 'full-bleed';

  useEffect(() => {
    const handlePageNavigationScroll = (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return;
      }

      if (event.key !== 'PageDown' && event.key !== 'PageUp') {
        return;
      }

      if (event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }

      if (isEditableElement(event.target)) {
        return;
      }

      const direction = event.key === 'PageDown' ? 1 : -1;
      const viewportHeight = Math.max(320, window.innerHeight || document.documentElement.clientHeight || 0);
      const scrollAmount = Math.floor(viewportHeight * 0.9);

      const root = document.scrollingElement as HTMLElement | null;
      if (root) {
        const previousRootTop = root.scrollTop;
        root.scrollBy({ top: scrollAmount * direction, behavior: 'auto' });
        if (root.scrollTop !== previousRootTop) {
          event.preventDefault();
        }
      }
    };

    window.addEventListener('keydown', handlePageNavigationScroll, { capture: true });
    return () => {
      window.removeEventListener('keydown', handlePageNavigationScroll, { capture: true });
    };
  }, []);

  const mainStyles: React.CSSProperties = {
    position: 'relative',
    flex: 1,
    padding: isFullBleedMode
      ? '0'
      : '0 clamp(1.25rem, 4vw, 4rem) clamp(2.5rem, 6vw, 4.5rem) clamp(1.25rem, 4vw, 4rem)',
    paddingTop: isFullBleedMode ? '0' : (showHeader ? 'calc(var(--header-height) + 2rem)' : 'clamp(1rem, 2.2vw, 1.6rem)')
  };

  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        background: themeStyles.backgroundGradient,
        color: themeStyles.text,
        position: 'relative',
        overflowX: 'hidden',
        overflowY: 'clip'
      }}
      className={`layout-shell ${themeClasses.bg} animate-fade-in`}
    >
      <div className="layout-glow layout-glow--indigo animate-float" aria-hidden="true" />
      <div className="layout-glow layout-glow--cyan animate-float" aria-hidden="true" />
      {showHeader && <Header userRole={userRole} />}
      <main style={mainStyles} className={`layout-main ${themeClasses.text}`}>
        {isFullBleedMode ? (
          children
        ) : !useSurface ? (
          <div className={`layout-marketing-shell animate-fade-in-up ${isAuthMode ? 'layout-auth-shell' : ''}`}>{children}</div>
        ) : (
          <section className="layout-app-shell animate-fade-in-up animate-scale-in">
            {children}
          </section>
        )}
      </main>
    </div>
  );
});

Layout.displayName = 'Layout';

export default Layout;
